import { GoCardlessMandateSection } from './GoCardlessMandateSection'

/**
 * Payment Integration Section Wrapper Component
 *
 * Conditionally renders payment integration sections based on selected payment method.
 * Sits directly below the payment method dropdown in CustomerForm and shows the
 * appropriate linking interface.
 *
 * Conditional Rendering Logic:
 * - 'xero' → null (Xero contact linking is now at branch level, not customer level)
 * - 'gocardless' → GoCardlessMandateSection
 * - 'cash' → null (no additional UI needed)
 * - null/undefined → null (no payment method selected yet)
 *
 * @param {Object} props
 * @param {string|null} props.paymentMethod - 'xero' | 'cash' | 'gocardless' | null
 * @param {Object|null} props.odinCustomer - Full customer object (may not exist yet for new customers)
 * @param {string|null} props.gocardlessMandateId - From customer.gocardless_mandate_id
 * @param {string|null} props.gocardlessMandateStatus - From customer.gocardless_mandate_status
 * @param {string|null} props.gocardlessCustomerId - From customer.gocardless_customer_id
 * @param {Function} props.onMappingChange - Callback when any mapping changes
 * @returns {JSX.Element|null} Integration section or null
 */
export function PaymentIntegrationSection({
  paymentMethod,
  odinCustomer,
  gocardlessMandateId,
  gocardlessMandateStatus,
  gocardlessCustomerId,
  onMappingChange
}) {
  // Early return if no payment method selected
  if (!paymentMethod) {
    return null
  }

  // Xero contact linking is now at branch level - no customer-level UI needed
  if (paymentMethod === 'xero') {
    return null
  }

  // Render GoCardless mandate linking
  if (paymentMethod === 'gocardless') {
    return (
      <GoCardlessMandateSection
        mandateId={gocardlessMandateId}
        mandateStatus={gocardlessMandateStatus}
        customerId={gocardlessCustomerId}
        odinCustomer={odinCustomer}
        onMappingChange={onMappingChange}
      />
    )
  }

  // Cash payment method - no additional UI
  if (paymentMethod === 'cash') {
    return null
  }

  // Unknown payment method - should not happen
  return null
}

export default PaymentIntegrationSection
